import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  TextField,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import AttendanceService from '../../services/attendance.service';
import DataTable from '../../components/common/DataTable';

const AttendanceList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [attendances, setAttendances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchAttendances();
  }, [page, rowsPerPage]);

  const fetchAttendances = async () => {
    try {
      setLoading(true);
      const response = await AttendanceService.getAll({
        'pagination[page]': page + 1,
        'pagination[pageSize]': rowsPerPage,
        populate: 'employee',
        sort: 'date:desc',
      });
      setAttendances(response.data?.data || []);
      setTotalCount(response.data?.meta?.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching attendances:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this attendance record?')) {
      try {
        await AttendanceService.delete(id);
        fetchAttendances();
      } catch (error) {
        console.error('Error deleting attendance:', error);
      }
    }
  };

  const getStatusChip = (status: string) => {
    const colors: any = {
      present: 'success',
      late: 'warning',
      absent: 'error',
      halfDay: 'info',
    };
    return <Chip label={status} color={colors[status] || 'default'} size="small" />;
  };

  const columns = [
    {
      id: 'date',
      label: 'Date',
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'employee',
      label: 'Employee',
      format: (value: any) => {
        // Handle Strapi v4 relation structure: { data: { id, attributes: {...} } }
        if (value?.data?.attributes) {
          const { firstName, lastName } = value.data.attributes;
          return firstName && lastName ? `${firstName} ${lastName}` : '-';
        }
        // Handle case where data might be directly in attributes
        if (value?.attributes) {
          const { firstName, lastName } = value.attributes;
          return firstName && lastName ? `${firstName} ${lastName}` : '-';
        }
        // Handle case where it's already an object with firstName/lastName
        if (value?.firstName && value?.lastName) {
          return `${value.firstName} ${value.lastName}`;
        }
        return '-';
      },
    },
    { id: 'clockIn', label: 'Clock In' },
    { id: 'clockOut', label: 'Clock Out' },
    { id: 'workHours', label: 'Hours' },
    {
      id: 'status',
      label: 'Status',
      format: (value: string) => getStatusChip(value),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Attendance</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/attendance/create')}
        >
          Clock In
        </Button>
      </Stack>

      <DataTable
        columns={columns}
        data={attendances}
        loading={loading}
        onEdit={(id) => navigate(`/attendance/${id}/edit`)}
        onDelete={handleDelete}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={(newRows) => {
          setRowsPerPage(newRows);
          setPage(0);
        }}
      />
    </Box>
  );
};

export default AttendanceList;

